import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  FaCloud,
  FaServer,
  FaShieldAlt,
  FaArrowRight,
  FaUsers,
  FaGlobe,
  FaChartLine,
  FaCheckCircle,
  FaRocket,
  FaDesktop,
  FaNetworkWired,
  FaAngleRight
} from 'react-icons/fa';
import {
  LuServer, LuGlobe, LuSettings, LuBrainCircuit, LuFileText, LuCloud, LuShieldCheck,
  LuDatabase, LuChartColumn, LuBot, LuRocket, LuNetwork, LuBolt,
  LuCode, LuRefreshCw, LuShield
} from 'react-icons/lu';
import SEO from '../components/SEO/SEO';
import contentData from '../data/contentData.json';

const ServiceCard = ({ icon, title, description, id, categoryId, index }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative overflow-hidden"
    >
      {/* Background Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 blur-xl"></div>

      <div className="relative bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 h-full">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-blue-50/30 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

        <div className="relative z-10">
          {/* Icon with Enhanced Styling */}
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
            <div className="relative flex items-center justify-center w-20 h-20 text-white rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 group-hover:scale-110 transition-transform duration-300">
              <span className="text-3xl">{icon}</span>
            </div>
          </div>

          {/* Content */}
          <h3 className="mb-4 text-2xl font-bold text-gray-900 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
            {title}
          </h3>

          <p className="mb-8 text-gray-600 leading-relaxed line-clamp-3">
            {description}
          </p>

          {/* Enhanced CTA Button */}
          <Link
            to={`/services/aws-services/${id}`}
            className="group/btn inline-flex items-center px-6 py-3 font-semibold text-blue-600 bg-blue-50 border-2 border-blue-200 rounded-xl hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 hover:text-white hover:border-transparent transition-all duration-300 transform hover:scale-105"
          >
            <span>Learn More</span>
            <FaArrowRight className="ml-2 transform group-hover/btn:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-4 right-4 w-2 h-2 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
        <div className="absolute bottom-4 left-4 w-1 h-1 bg-purple-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
      </div>
    </motion.div>
  );
};

const CategorySection = ({ category, index }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      id={category.id}
      className="mb-20 pt-20 -mt-20"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
    >
      <div className="pt-4"></div>

      {/* Enhanced Section Header */}
      <div className="mb-12 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="inline-flex items-center bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-blue-200/50 rounded-full px-6 py-3 mb-6"
        >
          <span className="text-2xl mr-3">{category.icon}</span>
          <span className="text-blue-700 font-semibold">Category</span>
        </motion.div>

        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
          <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
            {category.category}
          </span>
        </h2>

        <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
      </div>

      {/* Services Grid */}
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {category.services.map((service, serviceIndex) => (
          <ServiceCard
            key={service.id || serviceIndex}
            icon={service.icon}
            title={service.title}
            description={service.description}
            id={service.id}
            categoryId={category.id}
            index={serviceIndex}
          />
        ))}
      </div>
    </motion.div>
  );
};

const CategoryNav = ({ categories, activeCategory, setActiveCategory }) => {
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-6 mb-8 sticky top-24">
      {/* Enhanced Header */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Service Categories</h3>
        <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
      </div>

      <ul className="space-y-3">
        {categories.map((category, index) => (
          <motion.li
            key={category.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <a
              href={`#${category.id}`}
              className={`group flex items-center py-4 px-4 rounded-xl transition-all duration-300 ${activeCategory === category.id
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform scale-105'
                : 'hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:scale-102'
                }`}
              onClick={() => setActiveCategory(category.id)}
            >
              <div className={`mr-3 text-2xl transition-transform duration-300 group-hover:scale-110 ${activeCategory === category.id ? 'text-white' : 'text-blue-600'
                }`}>
                {category.icon}
              </div>

              <div className="flex-1">
                <span className={`font-semibold ${activeCategory === category.id ? 'text-white' : 'text-gray-800'
                  }`}>
                  {category.category}
                </span>
              </div>

              <div className="flex items-center">
                <span className={`text-xs px-3 py-1 rounded-full font-medium ${activeCategory === category.id
                  ? 'bg-white/20 text-white'
                  : 'bg-blue-100 text-blue-700'
                  }`}>
                  {category.services.length}
                </span>
                <FaAngleRight className={`ml-2 transition-transform duration-300 ${activeCategory === category.id
                  ? 'text-white transform rotate-90'
                  : 'text-blue-500 group-hover:translate-x-1'
                  }`} />
              </div>
            </a>
          </motion.li>
        ))}
      </ul>
    </div>
  );
};

const ServicesPage = () => {
  const [activeCategory, setActiveCategory] = useState('');
  const location = useLocation();

  // Create icon mapping
  // Create icon mapping
  const iconMap = {
    // Categories
    'aws-cloud-services': <LuServer />,
    'multi-cloud': <LuGlobe />,
    'devops': <LuSettings />,
    'ai-ml': <LuBrainCircuit />,
    'case-studies': <LuFileText />,

    // AWS Services
    'cloud-migration': <LuCloud />,
    'cloud-security': <LuShieldCheck />,
    'cost-management': <LuDatabase />,
    'data-analytics': <LuChartColumn />,
    'generative-ai': <LuBot />,
    'managed-services': <LuSettings />,

    // Multi-Cloud Services
    // 'hybrid-cloud': <LuNetwork />,
    // 'cloud-optimization': <LuBolt />,
    // 'multi-cost-management': <LuChartColumn />,

    // DevOps Services
    'cloud-devops': <LuSettings />,
    'iac': <LuCode />,
    'cicd': <LuRefreshCw />,

    // AI Services (if appearing here)
    'aws-ai': <LuBrainCircuit />,

    // Case Studies
    'enterprise-cloud-migration': <LuServer />,
    'devops-implementation': <LuSettings />,
    'cloud-security-overhaul': <LuShield />,
    'explore-all-case-studies': <LuRocket />

  };

  // Filter out software-development and map icons
  const filteredServices = contentData.services
    .filter(service => service.id !== 'software-development')
    .map(category => ({
      ...category,
      icon: iconMap[category.id] || category.icon,
      services: category.services.map(service => ({
        ...service,
        icon: iconMap[service.id] || service.icon
      }))
    }));

  // SEO data for Services page
  const seoData = {
    title: "AWS Cloud Services - Solutions",
    description: `Comprehensive AWS cloud services by ${contentData.company.name}. AWS cloud migration, DevOps automation, cloud security, data analytics, and AI solutions. Expert cloud consulting with ${contentData.company.client_satisfaction} client satisfaction.`,
    keywords: "AWS Cloud Services, Cloud Migration, DevOps, Cloud Security, Data Analytics, AI Solutions, Cloud Consulting"
  };

  useEffect(() => {
    // Extract hash from URL and scroll to that section
    const hash = location.hash.substring(1);
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        // Wait for render to complete
        setTimeout(() => {
          window.scrollTo({
            top: element.offsetTop - 100, // Adjust for header height
            behavior: 'smooth'
          });
          setActiveCategory(hash);
        }, 100);
      }
    } else if (filteredServices.length > 0) {
      // Set first category as active if no hash
      setActiveCategory(filteredServices[0].id);
    }
  }, [location]);

  // Update active category on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;

      for (const category of filteredServices) {
        const element = document.getElementById(category.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            if (activeCategory !== category.id) {
              setActiveCategory(category.id);
              // Update URL hash without scrolling
              window.history.replaceState(null, null, `#${category.id}`);
            }
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeCategory]);

  return (
    <>
      <SEO {...seoData} />

      {/* Enhanced Hero Section */}
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.4, 0.7, 0.4]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2
            }}
          />
        </div>

        {/* AWS Partner Badge - Right Side, Centered in Hero (Desktop Only) */}
        <motion.div
          className="hidden md:flex absolute right-6 lg:right-16 top-1/2 -translate-y-1/2 z-20"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.div
            className="bg-white/95 backdrop-blur-sm rounded-2xl p-3 shadow-2xl border border-white/30"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            <img
              src="/assets/AWS-Partner-Advanced-Tier-Services 1 (1) (1).jpg"
              alt="AWS Partner Advanced Tier Services"
              className="w-16 md:w-24 lg:w-28 h-auto rounded-lg shadow-sm"
            />
          </motion.div>
        </motion.div>

        <div className="container relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-6 py-3 mb-8"
            >
              <FaCloud className="text-blue-400 mr-2" />
              <span className="text-white font-semibold">Our Services</span>
            </motion.div>

            {/* Mobile-only AWS Partner Badge */}
            <motion.div
              className="md:hidden flex justify-center mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="bg-white/95 backdrop-blur-sm rounded-xl p-2 shadow-lg border border-white/30">
                <img
                  src="/assets/AWS-Partner-Advanced-Tier-Services 1 (1) (1).jpg"
                  alt="AWS Partner Advanced Tier Services"
                  className="w-20 h-auto rounded-lg shadow-sm"
                />
              </div>
            </motion.div>

            <h1 className="mb-8 font-bold text-white leading-tight">
              <span className="block mb-2 text-2xl md:text-6xl lg:text-7xl text-blue-100/90 font-medium md:font-bold">AWS Cloud Services</span>
              <span className="block text-5xl md:text-6xl lg:text-7xl bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent pt-1">
                That Transform
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              AWS Cloud Solutions tailored to meet the unique needs of your business.
              From strategy to implementation and ongoing management, we've got you covered.
            </p>

            {/* Enhanced Stats */}
            <motion.div
              className="grid grid-cols-3 gap-8 mt-16"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">20+</div>
                <div className="text-blue-200">AWS Cloud Services</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">99%</div>
                <div className="text-blue-200">Client Satisfaction</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">24/7</div>
                <div className="text-blue-200">Support</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Services Sections */}
      <section className="relative py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.05),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.05),transparent_50%)]"></div>

        <div className="container relative z-10">
          <div className="grid gap-12 lg:grid-cols-4">
            {/* Enhanced Left Sidebar Nav */}
            <div className="lg:col-span-1">
              <CategoryNav
                categories={filteredServices}
                activeCategory={activeCategory}
                setActiveCategory={setActiveCategory}
              />
            </div>

            {/* Service Categories Content */}
            <div className="lg:col-span-3">
              {filteredServices.map((category, index) => (
                <CategorySection key={category.id || index} category={category} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Why Choose Our Services */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-40 left-20 w-64 h-64 bg-blue-500/10 rounded-full blur-2xl"
            animate={{ rotate: 360 }}
            transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-80 h-80 bg-purple-500/10 rounded-full blur-2xl"
            animate={{ rotate: -360 }}
            transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          />
        </div>

        <div className="container relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Why Choose
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Our Services
              </span>
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              We deliver exceptional value through our expertise, personalized approach, and commitment to your success.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: FaRocket, title: 'Expert Team', desc: 'Certified professionals with extensive experience in cloud technologies', color: 'from-blue-500 to-cyan-500' },
              { icon: FaChartLine, title: 'Proven Results', desc: 'Track record of successful implementations across various industries', color: 'from-purple-500 to-pink-500' },
              { icon: FaNetworkWired, title: 'Custom Solutions', desc: 'Tailored approach to address your specific challenges and objectives', color: 'from-green-500 to-teal-500' },
              { icon: FaDesktop, title: 'Ongoing Support', desc: 'Dedicated assistance to ensure your cloud environment runs optimally', color: 'from-orange-500 to-red-500' }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="group relative"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                {/* Background Glow */}
                <div className={`absolute inset-0 bg-gradient-to-r ${item.color} opacity-0 group-hover:opacity-20 rounded-3xl blur-xl transition-all duration-500`}></div>

                <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300">
                  <motion.div
                    className={`w-20 h-20 bg-gradient-to-r ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}
                    animate={{
                      rotate: [0, 5, 0, -5, 0]
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: index * 0.5
                    }}
                  >
                    <item.icon className="text-white text-3xl" />
                  </motion.div>

                  <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
                  <p className="text-blue-100 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="relative py-24 bg-gradient-to-br from-blue-600 via-purple-600 to-cyan-600 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(-45deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:60px_60px]"
            animate={{ backgroundPosition: ['0px 0px', '60px 60px'] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />
        </div>

        <div className="container relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="mb-8 text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Transform Your Business?
            </h2>
            <p className="mb-12 text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Contact us today to discuss how our cloud services can help you achieve your business goals.
            </p>

            <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
              <Link
                to="/contact"
                className="group inline-flex items-center px-10 py-5 bg-white text-blue-900 font-bold rounded-2xl shadow-2xl hover:shadow-white/25 transition-all duration-300 transform hover:-translate-y-2 text-lg"
              >
                <span>Get Started</span>
                <FaArrowRight className="ml-3 transform group-hover:translate-x-1 transition-transform duration-200" />
              </Link>

              <div className="flex flex-col lg:flex-row items-center gap-6 text-xl font-semibold">
                <motion.a
                  href={`mailto:${contentData.company.contact.email}`}
                  className="text-white/90 hover:text-white transition-colors duration-200 flex items-center group"
                  whileHover={{ scale: 1.05 }}
                >
                  <FaUsers className="mr-2 group-hover:scale-110 transition-transform duration-200" />
                  {contentData.company.contact.email}
                </motion.a>

                <motion.a
                  href={`tel:${contentData.company.contact.phone}`}
                  className="text-white/90 hover:text-white transition-colors duration-200 flex items-center group"
                  whileHover={{ scale: 1.05 }}
                >
                  <FaGlobe className="mr-2 group-hover:scale-110 transition-transform duration-200" />
                  {contentData.company.contact.phone}
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;